import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MangaCard from '@/components/feature/MangaCard';
import { mangas, categories } from '@/mocks/manga';

const homeJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': 'https://comicniche.github.io/#organization',
      name: '日漫屋',
      url: 'https://comicniche.github.io/',
      logo: 'https://comicniche.github.io/logo.png',
      description: '聚合日漫字幕组最新译制资源的日漫汉化在线阅读平台',
    },
    {
      '@type': 'WebSite',
      '@id': 'https://comicniche.github.io/#website',
      url: 'https://comicniche.github.io/',
      name: '日漫屋 - 日漫汉化在线看',
      inLanguage: 'zh-CN',
      publisher: { '@id': 'https://comicniche.github.io/#organization' },
    },
    {
      '@type': 'WebPage',
      '@id': 'https://comicniche.github.io/#webpage',
      url: 'https://comicniche.github.io/',
      name: '日漫汉化在线看',
      isPartOf: { '@id': 'https://comicniche.github.io/#website' },
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: '日漫屋支持哪些阅读版本？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '日漫屋为每部连载漫画提供简体、繁体及双语对照三种阅读版本，点开即可自由切换，满足不同读者的阅读习惯。',
          },
        },
        {
          '@type': 'Question',
          name: '如何快速找到最新更新的日漫汉化？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '在首页"最新更新"板块可查看各作品的最新章节更新情况，也可以使用顶部搜索框或按分类浏览，轻松追更不迷路。',
          },
        },
        {
          '@type': 'Question',
          name: '日漫双语对照阅读有什么优势？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '双语对照让读者在欣赏剧情的同事学习日语原文，更适合日语学习者与进阶读者提升语感，享受原汁原味的阅读体验。',
          },
        },
      ],
    },
  ],
};

export default function Home() {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) navigate(`/category/0?q=${encodeURIComponent(query.trim())}`);
  };

  const hotMangas = mangas.filter((m) => m.isHot);
  const newMangas = [...mangas].sort((a, b) => b.latestChapter - a.latestChapter).slice(0, 6);
  const dualMangas = mangas.filter((m) => m.version.includes('双语')).slice(0, 4);

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(homeJsonLd) }} />
      <Navbar />

      {/* Hero */}
      <section className="relative flex h-[520px] w-full items-center justify-center md:h-[600px]">
        <img
          src="https://readdy.ai/api/search-image?query=stylized%20anime%20fantasy%20background%20of%20a%20night%20sky%20filled%20with%20stars%20and%20floating%20comic%20panels%2C%20warm%20crimson%20and%20gold%20neon%20glow%20with%20deep%20shadow%2C%20artistic%20abstract%20manga%20illustration%20style%2C%20cinematic%20atmosphere%2C%20high%20detail%2C%20dramatic%20lighting&width=1600&height=900&seq=hero-2026-x1&orientation=landscape"
          alt="日漫汉化在线阅读平台主视觉"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        <div className="relative z-10 flex w-full flex-col items-center px-4 text-center md:px-6">
          <span className="mb-4 inline-flex items-center gap-2 rounded-full bg-background-50/15 px-4 py-1.5 text-sm text-background-50 backdrop-blur">
            <i className="ri-fire-line text-amber-400"></i> 日漫字幕组同步译制 · 持续更新
          </span>
          <h1 className="font-heading max-w-3xl text-3xl font-bold leading-tight text-background-50 md:text-5xl">
            日漫汉化在线看，简体繁体双语对照
          </h1>
          <p className="mt-4 max-w-2xl text-sm text-background-100/90 md:text-base">
            聚合日漫字幕组最新译制资源，热门连载同步翻译，点开即选版本，追更不迷路。
          </p>
          <form
            onSubmit={handleSearch}
            className="mt-7 flex w-full max-w-md items-center gap-2 rounded-full bg-background-50/95 p-1.5 shadow-sm"
          >
            <i className="ri-search-line pl-3 text-foreground-500"></i>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索你想看的日漫汉化…"
              className="w-full bg-transparent py-2 text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
              aria-label="搜索漫画"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors"
            >
              开始搜索
            </button>
          </form>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <Link to="/category/0" className="rounded-full border border-background-50/30 px-4 py-1.5 text-sm text-background-50 hover:bg-background-50/15 transition-colors">
              浏览全部漫画
            </Link>
            <Link to="/about" className="rounded-full border border-background-50/30 px-4 py-1.5 text-sm text-background-50 hover:bg-background-50/15 transition-colors">
              关于日漫屋
            </Link>
          </div>
        </div>
      </section>

      {/* Category entry */}
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6" aria-labelledby="cat-title">
        <div className="mb-6 flex items-center justify-between">
          <h2 id="cat-title" className="flex items-center gap-2 text-xl font-bold text-foreground-950 md:text-2xl">
            <i className="ri-grid-fill text-primary-500"></i> 漫画分类
          </h2>
          <Link to="/category/0" className="text-sm text-primary-700 hover:text-primary-800 whitespace-nowrap">
            查看全部 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
          {categories.map((c) => (
            <Link
              key={c.id}
              to={`/category/${c.id}`}
              className="group flex flex-col items-center rounded-lg border border-background-200/70 bg-background-100 px-3 py-5 text-center transition-colors hover:border-primary-300"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-primary-700 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                <i className="ri-bookmark-line text-lg"></i>
              </span>
              <span className="mt-3 text-sm font-semibold text-foreground-950">{c.name}</span>
              <span className="mt-1 hidden text-xs text-foreground-500 lg:block">{c.desc}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Hot section */}
      <section className="mx-auto max-w-7xl px-4 pb-4 md:px-6" aria-labelledby="hot-title">
        <div className="mb-6 flex items-center justify-between">
          <h2 id="hot-title" className="flex items-center gap-2 text-xl font-bold text-foreground-950 md:text-2xl">
            <i className="ri-fire-fill text-accent-500"></i> 热门连载
          </h2>
          <Link to="/category/0" className="text-sm text-primary-700 hover:text-primary-800 whitespace-nowrap">
            更多热门 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {hotMangas.map((m) => (
            <MangaCard key={m.id} manga={m} />
          ))}
        </div>
      </section>

      {/* Latest updates */}
      <section className="bg-background-100 py-12" aria-labelledby="new-title">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="mb-6 flex items-center justify-between">
            <h2 id="new-title" className="flex items-center gap-2 text-xl font-bold text-foreground-950 md:text-2xl">
              <i className="ri-flashlight-fill text-primary-500"></i> 最新更新
            </h2>
            <Link to="/category/0" className="text-sm text-primary-700 hover:text-primary-800 whitespace-nowrap">
              全部更新 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {newMangas.map((m) => (
              <div
                key={m.id}
                className="flex gap-4 rounded-lg border border-background-200/70 bg-background-50 p-3"
              >
                <Link to={`/article/${m.id}`} className="relative w-24 shrink-0 overflow-hidden rounded-md bg-background-100">
                  <img
                    src={m.cover}
                    alt={`${m.title}封面`}
                    title={`${m.title}日漫汉化`}
                    className="aspect-[3/4] h-full w-full object-cover object-top"
                  />
                </Link>
                <div className="flex min-w-0 flex-1 flex-col">
                  <Link to={`/article/${m.id}`} className="truncate text-sm font-semibold text-foreground-950 hover:text-primary-700 transition-colors">
                    {m.title}
                  </Link>
                  <p className="mt-0.5 text-xs text-foreground-500">{m.author}</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {m.version.map((v) => (
                      <span key={v} className="rounded bg-secondary-100 px-1.5 py-0.5 text-[11px] text-secondary-900">
                        {v}
                      </span>
                    ))}
                  </div>
                  <Link
                    to={`/article/${m.id}`}
                    className="mt-auto flex items-center justify-between rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 hover:bg-primary-600 transition-colors"
                  >
                    <span>第 {m.latestChapter} 话</span>
                    <i className="ri-play-fill"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dual version */}
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6" aria-labelledby="dual-title">
        <div className="mb-6 flex items-center justify-between">
          <h2 id="dual-title" className="flex items-center gap-2 text-xl font-bold text-foreground-950 md:text-2xl">
            <i className="ri-stack-line text-accent-500"></i> 双语对照推荐
          </h2>
          <Link to="/category/0" className="text-sm text-primary-700 hover:text-primary-800 whitespace-nowrap">
            全部双语 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {dualMangas.map((m) => (
            <MangaCard key={m.id} manga={m} />
          ))}
        </div>
      </section>

      {/* Feature strip */}
      <section className="bg-secondary-100/70 py-10">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 sm:grid-cols-3 md:px-6">
          {[
            { icon: 'ri-magic-line', title: '多版本对照', desc: '简体、繁体、双语一键切换，阅读更自由' },
            { icon: 'ri-refresh-line', title: '字幕组同步译制', desc: '热门连载紧跟最新话数，追更不迷路' },
            { icon: 'ri-community-line', title: '爱好者聚合社区', desc: '汇集优质字幕组资源，共同守护热爱' },
          ].map((f) => (
            <div key={f.title} className="flex items-start gap-4 rounded-lg bg-background-50 p-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent-500 text-background-50">
                <i className={`${f.icon} text-xl`}></i>
              </span>
              <div>
                <h3 className="text-base font-semibold text-foreground-950">{f.title}</h3>
                <p className="mt-1 text-sm text-foreground-600">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative mt-12 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=stylized%20anime%20banner%20of%20stacked%20manga%20books%20and%20open%20comic%20pages%20with%20warm%20golden%20light%20and%20cherry%20blossom%20petals%2C%20artistic%20abstract%20illustration%2C%20warm%20red%20and%20cream%20tones%2C%20high%20detail&width=1600&height=500&seq=cta-2026-x1&orientation=landscape"
          alt="加入日漫屋开始阅读"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 flex w-full flex-col items-center px-4 py-16 text-center md:px-6">
          <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">每一部好漫画，都值得被看见</h2>
          <p className="mt-3 max-w-xl text-sm text-background-100/90 md:text-base">
            立即开始你的日漫汉化之旅，简体繁体双语随心切换，与千万读者一起追更。
          </p>
          <Link
            to="/category/0"
            className="mt-6 whitespace-nowrap rounded-full bg-primary-500 px-8 py-3 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors"
          >
            开始阅读
          </Link>
        </div>
      </section>

      <Footer />
    </>
  );
}